import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from "@angular/core";
import { ConstantsService } from "@app/services/constantsService";
import { IVButtonDropDownItem } from "@directives/_core/v-button/v-button.component";
import { SortDirection } from "@enums/SortDirection";
import { IPlot, IPlotTemperatureRecord } from "@interfaces/IPlot";
import { IProbe } from "@interfaces/IProbe";
import { IVehicle } from "@interfaces/IVehicle";
import { IVehicleInput } from "@interfaces/IVehicleInput";
import { ChartService, ChartType } from "@services/charts.service";
import { ColourService } from "@services/colour.service";
import { TranslationService } from "@services/translation.service";
import { UnitConversionService, UnitConversionType } from "@services/unitConversion.service";
import { VehicleService } from "@services/vehicle.service";
import { IVehicleInputRecordHistory, VehicleInputService } from "@services/vehicleInput.service";
import { VeriDate } from "@services/veriDate.service";
import { MultiStateInputCategory } from "@temperature/_enums/reefer/MultiStateInputCategory.enum";
import { ReeferZoneOperatingModeCategory } from "@temperature/_enums/reefer/ReeferZoneOperatingModeCategory.enum";
import { TemperatureProfileMaterialisationOptions } from "@temperature/_enums/temperature/TemperatureProfileMaterialisationOptions.enum";
import { IMultiStateInputWithHistories } from "@temperature/_models/multiStateInputs/IMultiStateInput";
import { IMultiStateInputHistory } from "@temperature/_models/multiStateInputs/IMultiStateInputHistory";
import { IInputCategories } from "@temperature/_models/temperature/IInputCategories";
import { ITemperatureProfile } from "@temperature/_models/temperature/ITemperatureProfile";
import { ITemperatureSetpoint } from "@temperature/_models/temperature/ITemperatureSetpoint";
import { ITemperatureSetpointHistory } from "@temperature/_models/temperature/ITemperatureSetpointHistory";
import { MultiStateInputService } from "@temperature/_services/multi-state-input.service";
import { TemperatureSetpointService } from "@temperature/_services/temperature-setpoint.service";
import { TemperatureService } from "@temperature/_services/temperature.service";
import { ZoneService } from "@temperature/_services/zone.service";
import { IPromise } from "angular";
import { lastValueFrom } from "rxjs";
import './temperature-graph.component.scss';

declare const Highcharts: any;

export interface IMultiStateInputHistoryChartData extends IMultiStateInputHistory {
    inputName: string;
}

export interface ITemperatureSetpointWithHistories extends ITemperatureSetpoint {
    histories?: ITemperatureSetpointHistory[];
}

export interface IProbeWithTemperatureData extends IProbe {
    records: IPlotTemperatureRecord[];
    temperatureProfile: ITemperatureProfile;
}

export interface IVehicleInputWithHistories extends IVehicleInput {
    histories: IVehicleInputRecordHistory[];
}

@Component({
    selector: 'temp-graph',
    template: require('./temperature-graph.component.html')
})
export class TemperatureGraphComponent2 implements OnChanges, OnDestroy {
    @Input() vehicle: IVehicle;
    @Input() start: Date;
    @Input() end: Date;
    @Input() plots: IPlot[];
    @Input() zoneId: number;
    @Input() showSourceDataBtn: boolean;

    @Output() showSourceData = new EventEmitter<void>();

    @ViewChild('tooltipElement', { read: ElementRef }) tooltipElement: ElementRef;

    loading = false;
    probeGraphLoading = false;
    inputChartLoading = false;

    showTooltip: boolean;
    showInputChart: boolean = true;
    showResetZoom: boolean;

    tooltipDate: string;
    lastUpdated: string;

    tooltipLeft: number;
    tooltipTop: number;
    tooltipWidth: number = 260;

    plotlinePosition: number;

    tempProfiles: ITemperatureProfile[] = [];

    tooltipInputPoints: any[] = [];
    tooltipMultiStateInputPoints: any[] = [];
    tooltipProbePoints: any[] = [];
    inputCategories: IInputCategories[] = [];

    probeGraphConfig: Highcharts.Options;
    inputChartConfig: Highcharts.Options;

    inputChart: Highcharts.ChartObject;
    probeGraph: Highcharts.ChartObject;

    exporting: boolean = false;
    exportTypes: IVButtonDropDownItem<Highcharts.ChartObject>[];
    showProbeConfigGraph: boolean = false;

    isMobile: boolean;

    colours = ["#0d3c55", "#0f5b78", "#117899", "#1395ba", "#5ca793", "#a2b86c", "#ebc844", "#ecaa38", "#ef8b2c", "#f16c20", "#d94e1f", "#c02e1d", "#c01d4e", "#b4189a", "#e42ed4", "#aa47dd"];

    constructor(
        private readonly chartService: ChartService,
        private readonly colourService: ColourService,
        private readonly constantsService: ConstantsService,
        private readonly multiStateInputService: MultiStateInputService,
        private readonly temperatureService: TemperatureService,
        private readonly temperatureSetpointService: TemperatureSetpointService,
        private readonly translationService: TranslationService,
        private readonly unitConversionService: UnitConversionService,
        private readonly vehicleInputService: VehicleInputService,
        private readonly vehicleService: VehicleService,
        private readonly zoneService: ZoneService,
        private readonly veriDate: VeriDate) {
        this.isMobile = this.constantsService.getIsMobile();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["vehicle"] || changes["start"] || changes["end"] || changes["zoneId"]) {
            this.resetState();
            this.setDefaultStartAndEndDates();
            this.loadGraphData();
        }
    }

    ngOnDestroy(): void {
        delete this.probeGraph;
        delete this.inputChart;
    }

    @HostListener('mousemove', ["$event"])
    tooltipMovement($event: MouseEvent): void {
        if (!this.probeGraphConfig) {
            return;
        }

        // min margin of yaxis label
        const yAxisMinLabelPadding = 80;

        //series label padding
        const seriesLabelPadding = this.probeGraphConfig.series[0]?.data ? 45 : 10;

        // min margin of xaxis label
        const XAxisLabelPadding = 41;

        if ($event.offsetX < yAxisMinLabelPadding || $event.offsetY <= seriesLabelPadding || this.tooltipProbePoints.length == 0 && this.tooltipInputPoints.length == 0 && this.tooltipMultiStateInputPoints.length == 0) {
            this.hideTooltip();
        }
        else {
            this.showTooltip = true;
        }

        const tooltipContainer = $event.currentTarget as HTMLElement;

        if (this.tooltipElement && this.tooltipElement.nativeElement) {
            const tooltipXOffset = 20;
            this.tooltipLeft = $event.offsetX + (tooltipXOffset);

            if (this.tooltipLeft + tooltipXOffset > tooltipContainer.offsetWidth - this.tooltipWidth) {
                this.tooltipLeft = $event.offsetX - (tooltipXOffset * 2);
                this.tooltipLeft -= this.tooltipWidth;
            }


            const tooltipYOffset = 50;
            // Todo: This is horribly inefficient - cache use of boundingClientRect outside of this method
            this.tooltipTop = $event.clientY - tooltipContainer.getBoundingClientRect().top - tooltipYOffset;
            this.tooltipTop = Math.min(this.tooltipTop, tooltipContainer.getBoundingClientRect().height - (this.tooltipElement.nativeElement.getBoundingClientRect().height + XAxisLabelPadding));
        }
    }

    hideTooltip(): void {
        this.showTooltip = false;
    }

    setPlotline($event: MouseEvent): void {
        const charts = [this.inputChart, this.probeGraph];
        charts.forEach(currentchart => {
            if (!currentchart || !currentchart.xAxis) {
                return;
            }

            const xaxis = currentchart.xAxis[0] as any;

            this.plotlinePosition = xaxis.toValue($event.offsetX, false);

            this.chartService.addOrUpdateChartCrossHairFromMouseX(xaxis, $event.offsetX);
            this.tooltipDate = this.veriDate.formatDate(new Date(this.plotlinePosition));
            this.updateTooltipData(currentchart, this.plotlinePosition);
        });
    }

    resetZoom(): void {
        const charts = [this.inputChart, this.probeGraph];

        charts.forEach(currentchart => {
            if (!currentchart || !currentchart.xAxis) {
                return;
            }

            currentchart.xAxis[0].setExtremes(null, null);

            this.showResetZoom = false;
        });
    }

    onItemClick(event: IVButtonDropDownItem<Highcharts.ChartObject>): void {
        this.exporting = true;

        const filename = `${this.vehicle.Reg} ${event.label} ${this.veriDate.formatDate(new Date(), 'Date')}`;

        event.data.exportChart({ filename });

        this.resetState();

        this.loadGraphData();
        this.exporting = false;
    }

    get showExportButton(): boolean {
        return !this.isMobile && (this.showProbeConfigGraph || this.showInputChart && !this.probeGraphLoading);
    }

    //--

    private resetState(): void {
        this.exportTypes = [];
        this.showProbeConfigGraph = false;
        this.showInputChart = false;

        delete this.probeGraph;
        delete this.inputChart;
        delete this.probeGraphConfig;
        delete this.inputChartConfig;
    }

    private loadGraphData(invalidateCache = false): void {

        if (this.latestRequested()) {
            invalidateCache = true;
        }

        this.probeGraphLoading = true;
        this.inputChartLoading = true;
        this.tempProfiles = [];

        if (!this.vehicle) {
            return;
        }

        const promises: any[] = [];

        promises.push(this.loadInputs(invalidateCache))
        promises.push(this.loadTemperatureData(invalidateCache));
        promises.push(this.loadMultiStateInputs());
        promises.push(this.temperatureSetpointService.loadSetpoints(this.vehicle.Id, this.start, this.end));

        Promise.all(promises)
            .then(([inputs, probes, multiStateInputs, setpoints]: [IVehicleInputWithHistories[], IProbeWithTemperatureData[], IMultiStateInputWithHistories[], ITemperatureSetpointWithHistories[]]) => {

                this.createProbeChart(probes, setpoints);
                this.createInputChart(inputs, multiStateInputs);
            })
            .finally(() => {
                this.showInputChart = this.inputChartConfig && this.inputChartConfig.series[0].data.length > 0;
                this.inputChartLoading = false;
                this.probeGraphLoading = false;

                if (invalidateCache) {
                    this.lastUpdated = new Date().toString();
                }
                else {
                    this.lastUpdated = null;
                }
            });
    }

    private loadTemperatureData(invalidateCache: boolean): Promise<IProbeWithTemperatureData[]> {
        const temperaturePromises: IPromise<any>[] = [];

        temperaturePromises.push(this.vehicleService.getProbes(this.vehicle.Id, invalidateCache));
        temperaturePromises.push(this.vehicleService.getVehicleTemperatureRecords(this.vehicle.Id, this.veriDate.createMoment(this.start), this.veriDate.createMoment(this.end), invalidateCache));
        temperaturePromises.push(this.temperatureService.getTemperatureProfiles(invalidateCache, { MaterialisationOptions: TemperatureProfileMaterialisationOptions.WithHistories }));

        return Promise.all(temperaturePromises).then(([probes, temperatureRecords, temperatureProfiles]: [IProbeWithTemperatureData[], IPlotTemperatureRecord[], ITemperatureProfile[]]) => {

            this.vehicleService.sanitiseVehicleProbes(this.vehicle, probes);

            temperatureRecords.sort((a, b) => {
                return this.veriDate.sort(a.Timestamp, b.Timestamp, SortDirection.Ascending);
            });

            probes.forEach(probe => {

                probe.records = [];

                temperatureRecords.forEach(record => {
                    if (record.TemperatureProbeId === probe.Id) {
                        probe.records.push(record);
                    }
                });

                const profile = temperatureProfiles.find(profile => probe.TemperatureProfileId === profile.Id);

                if (!profile) {
                    return;
                }

                let relevantProfile = profile.TemperatureProfileHistories.find(history => {
                    //Find relevant profile data for the time period that is being viewed
                    return this.veriDate.createMoment(this.start).isBetween(this.veriDate.createMoment(history.Start), this.veriDate.createMoment(history.End));
                }) as ITemperatureProfile;

                if (!relevantProfile) {
                    relevantProfile = profile;
                }
                relevantProfile.LowerBound = this.unitConversionService.convertTemperature(relevantProfile.LowerBound);
                relevantProfile.UpperBound = this.unitConversionService.convertTemperature(relevantProfile.UpperBound);
                relevantProfile.Name = profile.Name;

                probe.MinPrimaryThreshold = relevantProfile.LowerBound;
                probe.MaxPrimaryThreshold = relevantProfile.UpperBound;

                probe.temperatureProfile = relevantProfile;

                // If not added already, add to our list
                if (!this.tempProfiles.find(p => p.Id == relevantProfile.Id)) {
                    this.tempProfiles.push(relevantProfile);
                }
            });

            return probes;
        });
    }

    private loadMultiStateInputs(): Promise<IMultiStateInputWithHistories[]> {
        const multiStateInputPromises: Promise<any>[] = [];

        multiStateInputPromises.push(lastValueFrom(this.multiStateInputService.getMultiStateInputs({ VehicleId: this.vehicle.Id })));
        multiStateInputPromises.push(lastValueFrom(this.multiStateInputService.getMultiStateInputHistories({ VehicleId: this.vehicle.Id, Start: this.start, End: this.end })));

        return Promise.all(multiStateInputPromises).then(([multiStateInputs, multiStateInputHistories]: [IMultiStateInputWithHistories[], IMultiStateInputHistoryChartData[]]) => {
            multiStateInputs.forEach(multiStateInput => {
                multiStateInput.histories = multiStateInputHistories.filter(h => h.MultiStateInputId == multiStateInput.Id)
            });

            return multiStateInputs;
        });
    }

    private loadInputs(invalidateCache: boolean): Promise<IVehicleInputWithHistories[]> {

        const inputPromises: IPromise<any>[] = [];
        inputPromises.push(this.vehicleInputService.getVehicleInputs(this.vehicle.Id, invalidateCache));
        inputPromises.push(this.vehicleInputService.getVehicleInputHistory(this.vehicle.Id, this.start, this.end, true, invalidateCache));

        return Promise.all(inputPromises).then(([inputs, inputHistories]: [IVehicleInputWithHistories[], IVehicleInputRecordHistory[]]) => {
            inputs.forEach(input => {
                input.histories = inputHistories.filter(h => h.InputId == input.Id)
            });

            return inputs;
        });
    }


    private isInZone(zoneId: number): boolean {
        if (!isNaN(this.zoneId) && zoneId && zoneId === this.zoneId) {
            return true;
        } else if (!isNaN(this.zoneId)) {
            return false;
        }

        return true;
    }

    private addTemperatureSeries(temperatureSeries: any[], probe: IProbeWithTemperatureData, index: number): void {
        if (!this.isInZone(probe.ZoneId)) {
            return;
        }


        const foundRecords = [];
        var lastRecord: IPlotTemperatureRecord = null

        probe.records.forEach(record => {
            var timestampValue = new Date(record.Timestamp).valueOf();

            if (lastRecord != null && this.veriDate.getDiff(lastRecord.Timestamp, record.Timestamp, "hours") > 2) {
                foundRecords.push([new Date(timestampValue).valueOf(), null]);
            }

            foundRecords.push([timestampValue, this.unitConversionService.convertTemperature(record.Temperature)]);
            lastRecord = record;
        });

        const seriesColour = this.colourService.getVigetColor(index, 16, 3, this.colours);

        if (foundRecords.length > 0) {
            this.showProbeConfigGraph = true;
            temperatureSeries.push(
                {
                    name: probe.cleanName,
                    data: foundRecords,
                    id: probe.Id,
                    zoneAxis: 'y',
                    color: `${seriesColour}`,
                    lineWidth: 1,
                    zones: [
                        {
                            value: probe.MinPrimaryThreshold,
                            dashStyle: 'ShortDot',
                            color: seriesColour,
                        },
                        {
                            value: probe.MaxPrimaryThreshold,
                            dashStyle: 'solid',
                            color: seriesColour,
                        },
                        {
                            dashStyle: 'ShortDot',
                            color: seriesColour,
                        }
                    ]
                }
            );
        }
    }

    private addSetpointSeries(temperatureSeries: any[], setpoint: ITemperatureSetpointWithHistories): void {
        if (!setpoint.histories || !this.zoneId) {
            return;
        }

        if (!this.isInZone(setpoint.ZoneId)) {
            return;
        }

        const foundRecords = [];

        setpoint.histories.forEach(record => {
            if (this.veriDate.createMoment(record.StartTime).isBefore(this.start) && this.veriDate.createMoment(record.EndTime).isBefore(this.start)) {
                return;
            }

            if (this.veriDate.createMoment(record.StartTime).isBefore(this.start)) {
                foundRecords.push([this.start.valueOf(), this.unitConversionService.convertTemperature(record.SetpointValue)]);
            } else {
                foundRecords.push([new Date(record.StartTime).valueOf(), this.unitConversionService.convertTemperature(record.SetpointValue)]);
            }

            if (record.EndTime) {
                foundRecords.push([new Date(record.EndTime).valueOf(), this.unitConversionService.convertTemperature(record.SetpointValue)]);
            }
            else if (this.veriDate.createMoment(record.EndTime).isAfter(this.end)) {
                foundRecords.push([this.end.valueOf(), this.unitConversionService.convertTemperature(record.SetpointValue)]);
            }
            else {
                foundRecords.push([this.end.valueOf(), this.unitConversionService.convertTemperature(record.SetpointValue)]);
            }
        });

        const seriesColour = this.colourService.getVigetColor(16, 16, 3, this.colours);

        temperatureSeries.push(
            {
                name: this.translationService.t("temperatureGraph.setpoint"),
                data: foundRecords,
                id: setpoint.Id,
                zoneAxis: 'y',
                color: `${seriesColour}`,
                lineWidth: 1,
                dashStyle: "LongDash",
                showInLegend: false
            }
        );
    }

    private createProbeChart(probes: IProbeWithTemperatureData[], setpoints: ITemperatureSetpointWithHistories[]): void {
        if (!probes.length) {
            return;
        }

        const temperatureSeries: any[] = [];


        probes.forEach((probe, index) => {
            this.addTemperatureSeries(temperatureSeries, probe, index);
        });

        temperatureSeries.sort((a, b) => {
            //Ensure legend labels appear alphabetically
            if (a.name < b.name) {
                return -1;
            }
        })

        setpoints.forEach((setpoint, index) => {
            this.addSetpointSeries(temperatureSeries, setpoint);
        });

        this.probeGraphConfig = this.createTempChartConfig("line", temperatureSeries, chart => {
            this.probeGraph = chart;

            this.exportTypes.push({ id: 0, label: this.translationService.t("Common.Temperature"), icon: "panorama", data: this.probeGraph });
            this.sortExportLabels();

            setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            });
        });

        this.probeGraphConfig.exporting = this.getExportingOptions();

        this.chartService.setChartXAxisExtremes(this.probeGraphConfig, Number(this.end), Number(this.start));

        const series = this.probeGraphConfig.plotOptions as any;

        series.series = {
            states: {
                inactive: {
                    opacity: 1
                }
            },
            marker: {
                enabled: false,
                states: {
                    hover: {
                        enabled: false
                    }
                }
            }
        };

        this.probeGraphConfig.chart.marginLeft = 90;

        this.probeGraphConfig.chart.spacing = [10, 10, 10, 10];

        this.probeGraphConfig.chart.plotBackgroundColor = 'rgba(251,251,251,0.8)';

        this.probeGraphConfig.yAxis = {
            title: {
                text: `${this.translationService.t('Common.Temperature')} (${this.unitConversionService.getConversionLabel(UnitConversionType.Temperature)})`,
                margin: 20
            }
        };

        this.displayTempProfilesOnGraph();

        this.probeGraphConfig.chart.resetZoomButton = { position: { y: -100 } }; // hides reset button on the chart
        this.probeGraphConfig.tooltip = { enabled: false, shared: false };

        this.probeGraphConfig.legend = {
            enabled: true,
            layout: 'horizontal',
            align: 'left',
            verticalAlign: 'top',
            itemMarginTop: 0,
            itemMarginBottom: 0,
            width: 1000

        }

        this.syncChartZooming(this.probeGraphConfig);
        Highcharts.chart('tempChartContainer', this.probeGraphConfig);
    }

    private latestRequested(): boolean {
        const now = new Date();
        return this.veriDate.date1IsBeforeDate2(now, this.end)
    }

    private addInputSeries(inputs: IVehicleInputWithHistories[], categories: string[], seriesColour: string[], seriesData: any[]): void {
        inputs.forEach((vehicleInput, index) => {

            if (!this.isInZone(vehicleInput.ZoneId)) {
                return;
            }

            this.inputCategories.push({
                name: vehicleInput.name,
                inputStateDescription: vehicleInput.InputStateDescription,
                isMultiStateInput: false
            });

            categories.push(vehicleInput.name);

            seriesColour.push(this.colourService.getVigetColor(index, vehicleInput.histories.length, 7, this.colours));

            vehicleInput.histories.forEach(historyListItem => {
                historyListItem.inputName = vehicleInput.name;

                if (!historyListItem.inputName) {
                    return;
                }

                let x: number;
                let x2 = historyListItem.EndUtc ? new Date(historyListItem.EndUtc).valueOf() : Date.now();

                if (this.veriDate.date1IsBeforeDate2(historyListItem.StartUtc, this.start)) {
                    x = this.veriDate.createMoment(this.start).valueOf();
                }
                else {
                    x = this.veriDate.createMoment(historyListItem.StartUtc).valueOf();
                }

                if (this.veriDate.date1IsAfterDate2(historyListItem.EndUtc, this.end)) {
                    x2 = this.veriDate.createMoment(this.end).valueOf();
                }

                seriesData.push({
                    x: x,
                    x2: x2,
                    y: categories.indexOf(`${historyListItem.inputName}`),
                    name: `${historyListItem.inputName}`,
                    custom: {
                        inputStateDescription: vehicleInput.InputStateDescription,
                        isMultiStateInput: false
                    }
                });
            });
        });
    }

    private addMultiStateInputSeries(multiStateInputs: IMultiStateInputWithHistories[], categories: string[], seriesData: any[]): void {

        if (!this.zoneId) {
            // We only want multistate inputs on reefer pages
            return;
        }

        multiStateInputs.forEach(multiStateInput => {

            if (!this.isInZone(multiStateInput.ZoneId)) {
                return;
            }

            var inputCategoryName = this.translationService.translateEnum(MultiStateInputCategory, "MultiStateInputCategory", multiStateInput.Category);

            this.inputCategories.push({
                name: inputCategoryName,
                inputStateDescription: 1,
                isMultiStateInput: true
            });

            categories.push(inputCategoryName);

            multiStateInput.histories.forEach((historyListItem: IMultiStateInputHistoryChartData) => {
                let x: number;
                let x2 = historyListItem.EndTime ? new Date(historyListItem.EndTime).valueOf() : Date.now();

                if (this.veriDate.date1IsBeforeDate2(historyListItem.StartTime, this.start)) {
                    x = this.veriDate.createMoment(this.start).valueOf();
                }
                else {
                    x = this.veriDate.createMoment(historyListItem.StartTime).valueOf();
                }

                if (this.veriDate.date1IsAfterDate2(historyListItem.EndTime, this.end)) {
                    x2 = this.veriDate.createMoment(this.end).valueOf();
                }

                seriesData.push({
                    x: x,
                    x2: x2,
                    y: categories.indexOf(inputCategoryName),
                    name: inputCategoryName,
                    color: this.getColourForTriggerState(historyListItem, multiStateInputs),
                    custom: {
                        inputStateDescription: this.getDescriptionForTriggerState(historyListItem, multiStateInputs),
                        isMultiStateInput: true
                    }
                });
            });
        });
    }

    private createInputChart(inputs: IVehicleInputWithHistories[], multiStateInputs: IMultiStateInputWithHistories[]): void {

        if (!inputs.length) {
            return;
        }

        const categories: string[] = [];
        const xRangeSeries: any[] = [];
        const seriesData: any[] = [];
        const seriesColour: string[] = [];

        this.inputCategories = [];

        this.addInputSeries(inputs, categories, seriesColour, seriesData)
        this.addMultiStateInputSeries(multiStateInputs, categories, seriesData);

        this.orderInputCategoriesByName();

        xRangeSeries.push({
            name: `${this.vehicle.Reg}`,
            type: 'xrange',
            pointWidth: 20,
            pointPadding: 0.4,
            data: seriesData,
            showInLegend: false
        });

        this.inputChartConfig = this.chartService.createBasicXRangeChart(xRangeSeries);
        this.inputChartConfig.exporting = this.getExportingOptions();

        this.chartService.setChartXAxisExtremes(this.inputChartConfig, Number(this.end), Number(this.start));

        const yAxis = this.inputChartConfig.yAxis as Highcharts.AxisOptions;

        yAxis.categories = [...categories];
        yAxis.minPadding = 100;

        this.inputChartConfig.chart.marginLeft = 90;
        this.inputChartConfig.colors = seriesColour;

        var maxSeries = xRangeSeries[0].data.reduce((count, d) => { return Math.max(count, d.y + 1) }, 0);
        this.inputChartConfig.chart.height = (maxSeries + 1) * 35;

        this.chartService.setOnLoadCallback(this.inputChartConfig, (chart) => {
            this.inputChart = chart;

            this.exportTypes.push({ id: 1, label: this.translationService.t("Common.Inputs"), icon: "panorama", data: this.inputChart });
            this.sortExportLabels();

            setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 1000);
        });

        this.inputChartConfig.chart.resetZoomButton = { position: { y: -100 } }; // hides reset button on the chart

        this.syncChartZooming(this.inputChartConfig);
        Highcharts.chart('inputChartContainer', this.inputChartConfig);
    }

    private createTempChartConfig(chartType: ChartType, series: Highcharts.IndividualSeriesOptions[], callback: (chartObject: Highcharts.ChartObject, event: MouseEvent) => void): Highcharts.Options {
        const options = this.chartService.createChartOptions(chartType);
        options.series = series;

        options.chart.spacing = [0, 0, 0, 0];
        options.chart.marginRight = 0;

        this.chartService.setOnLoadCallback(options, callback);
        return options;
    }

    private sortExportLabels(): void {
        this.exportTypes.sort((a, b) => {
            return Number(a.label) - Number(b.label);
        });
    }

    private getExportingOptions(): Highcharts.ExportingOptions {
        return {
            enabled: true,
            buttons: null,
            chartOptions: {
                title: {
                    text: this.vehicle.Reg
                },
                chart: {
                    backgroundColor: 'white'
                }
            }
        }
    }

    private displayTempProfilesOnGraph(): void {

        this.tempProfiles.forEach((profile, index) => {
            const yaxis = this.probeGraphConfig.yAxis as Highcharts.AxisOptions;

            yaxis.plotBands = yaxis.plotBands || [];
            yaxis.plotLines = yaxis.plotLines || [];

            const seriesColour = this.colourService.getVigetColor(index, 4, 3);

            yaxis.plotBands.push({
                from: profile.LowerBound,
                to: profile.UpperBound,
                id: profile.Name,
                color: this.colourService.createTransparentColor(seriesColour, 0.08),
            });

            yaxis.plotLines.push({
                value: profile.LowerBound,
                color: 'rgba(145,145,145, 0.4)',
                dashStyle: 'longdash',
                width: 1,
                zIndex: 3,
                label: {
                    text: `${profile.Name} Min`,
                    style: {
                        color: '#919191',
                        fontSize: "12",
                        fontWeight: "400"
                    }
                }
            });

            yaxis.plotLines.push({
                value: profile.UpperBound,
                color: 'rgba(145,145,145, 0.4)',
                dashStyle: 'longdash',
                width: 1,
                zIndex: 3,
                label: {
                    text: `${profile.Name} Max`,
                    y: 15,
                    style: {
                        color: '#919191',
                        fontSize: "12",
                        fontWeight: "400"
                    }
                }
            });
        });
    }

    private syncChartZooming(currentChart: Highcharts.Options): void {
        this.showResetZoom = false;
        const xAxis = currentChart.xAxis as Highcharts.AxisOptions;
        xAxis.startOnTick = false;
        xAxis.endOnTick = false;

        xAxis.events = {
            afterSetExtremes: (event: any) => {
                if (event.userMax) {
                    this.showResetZoom = true;
                }

                if (event.trigger == "zoom") {
                    if (this.inputChart && currentChart == this.probeGraphConfig) {
                        this.inputChart.xAxis[0].setExtremes(event.min, event.max, true, true);
                    }

                    if (currentChart == this.inputChartConfig) {
                        this.probeGraph.xAxis[0].setExtremes(event.min, event.max, true, true);
                    }
                }
            }
        };
    }

    private orderInputCategoriesByName(): void {
        this.inputCategories.sort((a, b) => {
            if (a.name < b.name) {
                return -1;
            }
        })
    }

    private orderTooltipInputPointsByName(): void {
        this.tooltipInputPoints.sort((a, b) => {
            if (a.name < b.name) {
                return -1;
            }
        })
    }

    private orderTooltipMultiStateInputPointsByName(): void {
        this.tooltipMultiStateInputPoints.sort((a, b) => {
            if (a.name < b.name) {
                return -1;
            }
        })
    }

    private updateTooltipData(chart: Highcharts.ChartObject, axisPoint: number): void {
        const xaxis = chart.xAxis[0] as any;

        if (chart == this.probeGraph) {
            this.tooltipProbePoints = [];

            xaxis.series.forEach(seriesItem => {
                const foundProbePoint = seriesItem.data.find(p => p && p.x >= axisPoint);

                if (foundProbePoint) {
                    this.tooltipProbePoints.push(foundProbePoint);
                }
            });
        }

        if (chart == this.inputChart) {
            this.tooltipInputPoints = [];

            xaxis.series.forEach(seriesItem => {
                seriesItem.data.forEach(point => {
                    if (axisPoint >= point.x && axisPoint <= point.x2) {
                        this.tooltipInputPoints.push(point);
                    }
                });
            });
            if (this.tooltipInputPoints.length) {
                this.orderTooltipInputPointsByName();
            }
        }
    }

    private getColourForTriggerState(historyListItem: IMultiStateInputHistoryChartData, multiStateInputs: IMultiStateInputWithHistories[]) {
        const multiStateInput = multiStateInputs.find(input => {
            return input.Id === historyListItem.MultiStateInputId;
        });

        if (multiStateInput && multiStateInput.Category == MultiStateInputCategory.ReeferZoneOperatingMode) {
            var zoneOperatingMode = historyListItem.TriggerState as ReeferZoneOperatingModeCategory;
            return this.zoneService.getOperatingModeColour(zoneOperatingMode);
        }

        return this.zoneService.getOperatingModeColour(-1);
    }

    private getDescriptionForTriggerState(historyListItem: IMultiStateInputHistoryChartData, multiStateInputs: IMultiStateInputWithHistories[]) {

        const multiStateInput = multiStateInputs.find(input => {
            return input.Id === historyListItem.MultiStateInputId;

        });
        if (multiStateInput) {
            return this.multiStateInputService.getTriggerStateName(historyListItem.TriggerState, multiStateInput.Category);
        }
    }

    private setDefaultStartAndEndDates(): void {
        if (!this.start || !this.end) {
            this.end = this.veriDate.createMoment(new Date()).toDate();
            this.start = this.veriDate.createMoment(this.end).subtract(1, "day").toDate();
        }
    }
}
